import type { WorkspaceRoleListItemDto } from '@up2stay/up2promo-api-client';
import { WorkspaceMembersApi } from '@up2stay/up2promo-api-client';
import {
  Badge,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@up2stay/up2promo-ui';
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react';
import { useIonViewWillEnter } from '@ionic/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  resolveRoleListItemDescription,
  resolveRoleListItemName,
} from '@/core/authz/role-assignment';
import { UpgradeCard } from '@/core/components/billing/upgrade-surfaces';
import {
  SettingsGroup,
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { DeleteRoleDialog } from '@/core/components/workspace/roles/delete-role-dialog';
import { RoleEditor } from '@/core/components/workspace/roles/role-editor';
import { useEntitlements } from '@/core/hooks/use-entitlements';
import { useIsMobile } from '@/core/hooks/use-mobile';
import { useWorkspaceRoles } from '@/core/hooks/use-workspace-roles';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { useCurrentWorkspace } from '@/core/store/workspace.store';

import { countMembersByRole } from './role-member-counts';

async function fetchMemberCountsByRole(
  workspaceId: string,
): Promise<Record<string, number>> {
  const counts: Record<string, number> = {};
  let page = 1;
  let hasNext = true;

  while (hasNext) {
    const response = await WorkspaceMembersApi.findAll({
      path: { workspaceId },
      query: { page, limit: 100 },
    });
    countMembersByRole(response.data?.data ?? [], counts);
    hasNext = response.data?.hasNextPage ?? false;
    page += 1;
    if (page > 50) break;
  }

  return counts;
}

type RoleRowActionsProps = {
  role: WorkspaceRoleListItemDto;
  canDelete: boolean;
  canEditCustom: boolean;
  customRolesEnabled: boolean;
  memberCount: number;
  viewLabel: string;
  editLabel: string;
  viewLockedLabel: string;
  deleteLabel: string;
  cannotDeleteInUse: string;
  onEdit: (role: WorkspaceRoleListItemDto) => void;
  onDelete: (role: WorkspaceRoleListItemDto) => void;
};

function RoleRowActions({
  role,
  canDelete,
  canEditCustom,
  customRolesEnabled,
  memberCount,
  viewLabel,
  editLabel,
  viewLockedLabel,
  deleteLabel,
  cannotDeleteInUse,
  onEdit,
  onDelete,
}: RoleRowActionsProps) {
  return (
    <div className="flex shrink-0 items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => onEdit(role)}
        title={
          role.isSystem
            ? viewLabel
            : canEditCustom
              ? editLabel
              : viewLockedLabel
        }
      >
        {role.isSystem || !canEditCustom ? (
          <Eye className="h-4 w-4" />
        ) : (
          <Pencil className="h-4 w-4" />
        )}
      </Button>
      {!role.isSystem ? (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-destructive"
          onClick={() => {
            if (!canDelete) {
              toast.error(
                cannotDeleteInUse.replace('{count}', String(memberCount)),
              );
              return;
            }
            onDelete(role);
          }}
          disabled={!customRolesEnabled}
          title={deleteLabel}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}

export function PeopleRolesTab() {
  const dict = useDictionary();
  const rolesDict = dict.workspace.roles;
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  /** Page composition only — not Cap shell chrome. */
  const isMobile = useIsMobile();

  const {
    data: roles = [],
    isLoading,
    refetch,
  } = useWorkspaceRoles(workspaceId);
  const { isLoading: entitlementsLoading, isFeatureEnabled } =
    useEntitlements();
  const customRolesEnabled = isFeatureEnabled('feature.custom_roles');

  const [memberCounts, setMemberCounts] = useState<Record<string, number>>({});
  const [countsLoading, setCountsLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRole, setSelectedRole] =
    useState<WorkspaceRoleListItemDto | null>(null);

  const loadMemberCounts = useCallback(async () => {
    if (!workspaceId) return;
    setCountsLoading(true);
    try {
      const counts = await fetchMemberCountsByRole(workspaceId);
      setMemberCounts(counts);
    } catch (error) {
      console.error('Failed to load member counts:', error);
    } finally {
      setCountsLoading(false);
    }
  }, [workspaceId]);

  const handleRefresh = () => {
    void refetch();
    void loadMemberCounts();
  };

  useEffect(() => {
    if (!workspaceId) return;
    void (async () => {
      await loadMemberCounts();
    })();
  }, [loadMemberCounts, roles.length, workspaceId]);

  useIonViewWillEnter(() => {
    handleRefresh();
  });

  const sortedRoles = useMemo(() => {
    const system = roles.filter((r) => r.isSystem);
    const custom = roles.filter((r) => !r.isSystem);
    return [...system, ...custom];
  }, [roles]);

  const getMemberCount = (role: WorkspaceRoleListItemDto): number =>
    memberCounts[role.id] ?? 0;

  const openCreate = () => {
    setSelectedRole(null);
    setEditorOpen(true);
  };

  const openEdit = (role: WorkspaceRoleListItemDto) => {
    setSelectedRole(role);
    setEditorOpen(true);
  };

  const openDelete = (role: WorkspaceRoleListItemDto) => {
    setSelectedRole(role);
    setDeleteOpen(true);
  };

  if (!currentWorkspace) {
    return (
      <p className="text-[13.5px] leading-relaxed text-muted-foreground">
        {rolesDict.selectWorkspace}
      </p>
    );
  }

  const showUpgrade = !entitlementsLoading && !customRolesEnabled;
  const tableLoading = isLoading || countsLoading;
  const builtinRoleLabels = dict.workspace.users.roles;
  const builtinRoleDescriptions = dict.workspace.users.roleDescriptions;

  const renderRoleActions = (role: WorkspaceRoleListItemDto) => {
    const count = getMemberCount(role);
    const canDelete = !role.isSystem && customRolesEnabled && count === 0;
    const canEditCustom = !role.isSystem && customRolesEnabled;

    return (
      <RoleRowActions
        role={role}
        canDelete={canDelete}
        canEditCustom={canEditCustom}
        customRolesEnabled={customRolesEnabled}
        memberCount={count}
        viewLabel={rolesDict.actions.view}
        editLabel={rolesDict.actions.edit}
        viewLockedLabel={rolesDict.actions.viewLocked}
        deleteLabel={rolesDict.actions.delete}
        cannotDeleteInUse={rolesDict.errors.cannotDeleteInUse}
        onEdit={openEdit}
        onDelete={openDelete}
      />
    );
  };

  return (
    <div className="flex flex-1 flex-col gap-4 md:gap-5">
      {showUpgrade && <UpgradeCard feature="feature.custom_roles" compact />}

      {/* No section label: the Roles tab already names this table (DEV-518). */}
      <section
        id="workspace-roles"
        aria-label={rolesDict.title}
        className="flex flex-col gap-2"
      >
        {!isMobile && customRolesEnabled ? (
          <div className="flex justify-end">
            <Button size="sm" className="min-h-9" onClick={openCreate}>
              <Plus className="mr-1.5 h-4 w-4" />
              {rolesDict.createRole}
            </Button>
          </div>
        ) : null}
        <SettingsGroup>
          {isMobile ? (
            <SettingsGroupRows>
              {tableLoading ? (
                [1, 2, 3].map((i) => (
                  <SettingsGroupRow key={i} separated={i > 1}>
                    <Skeleton className="h-12 w-full" />
                  </SettingsGroupRow>
                ))
              ) : sortedRoles.length === 0 && !customRolesEnabled ? (
                <SettingsGroupRow>
                  <p className="py-6 text-center text-[13.5px] leading-relaxed text-muted-foreground">
                    {rolesDict.table.empty}
                  </p>
                </SettingsGroupRow>
              ) : (
                sortedRoles.map((role, index) => {
                  const count = getMemberCount(role);
                  const permissionCount = role.permissions.length;
                  const displayName = resolveRoleListItemName(
                    role,
                    builtinRoleLabels,
                  );
                  const displayDescription = resolveRoleListItemDescription(
                    role,
                    builtinRoleDescriptions,
                  );

                  return (
                    <SettingsGroupRow key={role.id} separated={index > 0}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          {/* Name wraps and the badge moves below instead of
                              cutting the name to a few letters (DEV-821). */}
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            {!role.isSystem && role.color ? (
                              <span
                                className="inline-block size-3 shrink-0 rounded-full"
                                style={{ backgroundColor: role.color }}
                              />
                            ) : null}
                            <span className="min-w-0 text-[13.5px] font-semibold leading-relaxed text-foreground [overflow-wrap:anywhere]">
                              {displayName}
                            </span>
                            <Badge
                              variant={role.isSystem ? 'secondary' : 'outline'}
                              className="shrink-0"
                            >
                              {role.isSystem
                                ? rolesDict.badges.system
                                : rolesDict.badges.custom}
                            </Badge>
                          </div>
                          {displayDescription ? (
                            <p className="mt-0.5 line-clamp-2 text-[11px] leading-relaxed text-muted-foreground">
                              {displayDescription}
                            </p>
                          ) : null}
                          <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] leading-relaxed text-muted-foreground">
                            <span>
                              {rolesDict.table.permissions}: {permissionCount}
                            </span>
                            <span>
                              {rolesDict.table.members}: {count}
                            </span>
                          </div>
                        </div>
                        {renderRoleActions(role)}
                      </div>
                    </SettingsGroupRow>
                  );
                })
              )}
              {customRolesEnabled ? (
                <SettingsGroupRow
                  separated={tableLoading || sortedRoles.length > 0}
                >
                  <button
                    type="button"
                    onClick={openCreate}
                    className="flex min-h-9 w-full items-center gap-2 text-left text-[13.5px] font-medium leading-relaxed text-primary"
                  >
                    <Plus className="h-4 w-4 shrink-0" aria-hidden />
                    {rolesDict.createRole}
                  </button>
                </SettingsGroupRow>
              ) : null}
            </SettingsGroupRows>
          ) : (
            <div className="min-w-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{rolesDict.table.name}</TableHead>
                    <TableHead>{rolesDict.table.type}</TableHead>
                    <TableHead>{rolesDict.table.permissions}</TableHead>
                    <TableHead>{rolesDict.table.members}</TableHead>
                    <TableHead className="w-[100px]">
                      {rolesDict.table.actions}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableLoading ? (
                    [1, 2, 3].map((i) => (
                      <TableRow key={i}>
                        <TableCell colSpan={5}>
                          <Skeleton className="h-8 w-full" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : sortedRoles.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="py-8 text-center text-[13.5px] leading-relaxed text-muted-foreground"
                      >
                        {rolesDict.table.empty}
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedRoles.map((role) => {
                      const count = getMemberCount(role);
                      const permissionCount = role.permissions.length;
                      const displayName = resolveRoleListItemName(
                        role,
                        builtinRoleLabels,
                      );
                      const displayDescription = resolveRoleListItemDescription(
                        role,
                        builtinRoleDescriptions,
                      );

                      return (
                        <TableRow key={role.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {!role.isSystem && role.color ? (
                                <span
                                  className="inline-block size-3 rounded-full"
                                  style={{ backgroundColor: role.color }}
                                />
                              ) : null}
                              <span className="text-[13.5px] font-semibold leading-relaxed text-foreground">
                                {displayName}
                              </span>
                            </div>
                            {displayDescription ? (
                              <p className="mt-0.5 text-[11px] leading-relaxed text-muted-foreground">
                                {displayDescription}
                              </p>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={role.isSystem ? 'secondary' : 'outline'}
                            >
                              {role.isSystem
                                ? rolesDict.badges.system
                                : rolesDict.badges.custom}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-[13.5px] leading-relaxed text-muted-foreground">
                            {permissionCount}
                          </TableCell>
                          <TableCell className="text-[13.5px] leading-relaxed text-muted-foreground">
                            {count}
                          </TableCell>
                          <TableCell>{renderRoleActions(role)}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </SettingsGroup>
      </section>

      <RoleEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        workspaceId={currentWorkspace.id}
        role={selectedRole}
        dict={rolesDict.editor}
        permissionsDict={dict.permissions as Record<string, unknown>}
        builtinRoleLabels={builtinRoleLabels}
        builtinRoleDescriptions={builtinRoleDescriptions}
        onSuccess={handleRefresh}
        canEditCustomRoles={customRolesEnabled}
      />

      <DeleteRoleDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        workspaceId={currentWorkspace.id}
        role={selectedRole}
        roleDisplayName={
          selectedRole
            ? resolveRoleListItemName(selectedRole, builtinRoleLabels)
            : ''
        }
        memberCount={
          selectedRole && !selectedRole.isSystem
            ? getMemberCount(selectedRole)
            : 0
        }
        dict={rolesDict.deleteDialog}
        onSuccess={handleRefresh}
      />
    </div>
  );
}
